import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  vehicleGroupsApi,
  type VehicleGroupSlackConfig,
  type VehicleGroupSlackConfigInput,
} from '../../api/vehicle-groups';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { Field, inputClass } from '../../ui/Field';

/**
 * Slack-Channel einer Fahrzeuggruppe.
 *
 * **Warum diese Seite der fehlende Schlüssel war.** Zustellwege, Produzenten und
 * Adapter standen, aber `SlackChannel.postToChannel` stieg ohne eine Zeile in
 * `vehicle_group_slack_configs` sofort wieder aus — und es gab keinen Weg, eine
 * anzulegen. Darum kam in keinem Channel etwas an, obwohl alles verdrahtet war.
 *
 * Neben dem Berechtigungs-Editor und nicht unter „Integrationen": Die
 * Konfiguration gehört zu **einer** Gruppe, und wer hier festlegt, wer erwähnt
 * wird und ob über Slack freigegeben werden darf, trifft eine Entscheidung
 * derselben Art wie dort.
 */
export function SlackConfigPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: vehicleGroupsApi.list });
  const query = useQuery({
    queryKey: ['vehicle-group-slack', id],
    queryFn: () => vehicleGroupsApi.slackConfig(id),
  });

  const group = (groups.data ?? []).find((entry) => entry.id === id);

  return (
    <div className="space-y-4">
      <div>
        <Link to="/admin/vehicle-groups" className="text-xs text-slate-500 hover:text-slate-900">
          ← Fahrzeuggruppen
        </Link>
        <h2 className="text-base font-semibold">Slack{group ? ` · ${group.name}` : ''}</h2>
        <p className="max-w-3xl text-sm text-slate-500">
          In welchen Channel Freigabe-Anfragen dieser Gruppe gehen. Ohne Eintrag hier stellt die
          Gruppe nichts zu — die Entscheidung erreicht die buchende Person dann nur als
          Direktnachricht.
        </p>
      </div>

      <AsyncBoundary query={query}>
        {(config) => <SlackConfigForm groupId={id} config={config} onSaved={() => void queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] })} />}
      </AsyncBoundary>
    </div>
  );
}

/** Leerer Zustand = nicht eingerichtet. Die Vorgaben entsprechen der Entität. */
const EMPTY: VehicleGroupSlackConfigInput = {
  slackChannelId: '',
  approverSlackUserIds: [],
  approverSlackUserGroupIds: [],
  notifyOnNewBooking: true,
  notifyOnUnmatchedTrip: true,
  allowSlackApproval: true,
  allowSelfApproval: false,
};

function SlackConfigForm({
  groupId,
  config,
  onSaved,
}: {
  groupId: string;
  config: VehicleGroupSlackConfig | null;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<VehicleGroupSlackConfigInput>(config ?? EMPTY);

  // Nach dem Speichern liefert der Server den geprüften Stand zurück — unter
  // anderem die normalisierte Channel-ID. Der Entwurf zieht nach.
  useEffect(() => {
    if (config) setDraft(config);
  }, [config]);

  const save = useMutation({
    mutationFn: (body: VehicleGroupSlackConfigInput) =>
      vehicleGroupsApi.setSlackConfig(groupId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicle-group-slack', groupId] });
      onSaved();
    },
  });

  const remove = useMutation({
    mutationFn: () => vehicleGroupsApi.removeSlackConfig(groupId),
    onSuccess: async () => {
      setDraft(EMPTY);
      await queryClient.invalidateQueries({ queryKey: ['vehicle-group-slack', groupId] });
      onSaved();
    },
  });

  const busy = save.isPending || remove.isPending;

  return (
    <div className="max-w-2xl space-y-5 rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-medium">Channel</h3>
        {config ? (
          <Badge tone="emerald">eingerichtet</Badge>
        ) : (
          <Badge tone="neutral">nicht eingerichtet</Badge>
        )}
      </div>

      <Field
        label="Channel-ID"
        hint={
          config
            ? `Zuletzt geprüft: #${config.slackChannelName}. Die ID steht im Channel unter „Über" ganz unten.`
            : 'Die ID steht im Channel unter „Über" ganz unten — nicht der Name, der ändert sich.'
        }
      >
        <input
          value={draft.slackChannelId}
          onChange={(event) => setDraft({ ...draft, slackChannelId: event.target.value.trim() })}
          placeholder="C01ABC2DEF"
          disabled={busy}
          className={inputClass}
        />
      </Field>

      {/*
        Erwähnungen: IDs und nicht Namen, weil Slack in Nachrichten nur IDs
        auflöst. Die Usergruppe (`S…`) benachrichtigt alle ihre Mitglieder.
      */}
      <Field
        label="Zu erwähnende Konten"
        hint="Slack-IDs, mit Komma getrennt (U… für Personen). Sie werden unter der Karte erwähnt."
        optional
      >
        <input
          value={draft.approverSlackUserIds.join(', ')}
          onChange={(event) => setDraft({ ...draft, approverSlackUserIds: splitIds(event.target.value) })}
          placeholder="U01ABC2DEF, U02DEF3GHI"
          disabled={busy}
          className={inputClass}
        />
      </Field>

      <Field
        label="Zu erwähnende Usergruppen"
        hint="Slack-IDs, mit Komma getrennt (S…). Benachrichtigt alle Mitglieder der Gruppe."
        optional
      >
        <input
          value={draft.approverSlackUserGroupIds.join(', ')}
          onChange={(event) =>
            setDraft({ ...draft, approverSlackUserGroupIds: splitIds(event.target.value) })
          }
          placeholder="S01ABC2DEF"
          disabled={busy}
          className={inputClass}
        />
      </Field>

      <div className="space-y-2 border-t border-slate-100 pt-4">
        <Toggle
          checked={draft.notifyOnNewBooking}
          onChange={(value) => setDraft({ ...draft, notifyOnNewBooking: value })}
          disabled={busy}
          label="Bei neuen Freigabe-Anfragen in den Channel schreiben"
        />
        <Toggle
          checked={draft.notifyOnUnmatchedTrip}
          onChange={(value) => setDraft({ ...draft, notifyOnUnmatchedTrip: value })}
          disabled={busy}
          label="Bei Fahrten ohne zugeordnete Buchung melden"
          hint="Greift erst mit dem Fahrtenbuch (Etappe 8)."
        />
        <Toggle
          checked={draft.allowSlackApproval}
          onChange={(value) => setDraft({ ...draft, allowSlackApproval: value })}
          disabled={busy}
          label="Freigabe direkt in Slack erlauben"
          /*
            Die Begründung steht an der Entität und ist der Grund, dass es diesen
            Schalter überhaupt gibt: Eine Slack-Freigabe hat ein niedrigeres
            Identitäts-Assurance-Level als das Portal hinter Entra-SSO mit MFA.
          */
          hint="Abgeschaltet bleiben die Knöpfe ein Deep-Link ins Portal. Eine Slack-Sitzung ist langlebiger als eine Anmeldung mit MFA, und ein Workspace-Admin kann Konten verändern."
        />
        <Toggle
          checked={draft.allowSelfApproval}
          onChange={(value) => setDraft({ ...draft, allowSelfApproval: value })}
          disabled={busy || !draft.allowSlackApproval}
          label="Eigene Buchungen in Slack selbst freigeben"
          hint="Gilt nur für den Slack-Weg. Im Portal ist es unabhängig davon möglich."
        />
      </div>

      {save.isError && <ErrorNotice error={save.error} />}
      {remove.isError && <ErrorNotice error={remove.error} />}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          Gespeichert und bei Slack geprüft. Ob der Bot im Channel ist, zeigt der Testknopf unter
          „Integrationen" — <code className="font-mono text-xs">conversations.info</code> antwortet
          auch ohne Mitgliedschaft.
        </p>
      )}

      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={busy || draft.slackChannelId.length === 0}
          onClick={() => save.mutate(draft)}
        >
          {save.isPending ? 'Wird geprüft …' : 'Speichern'}
        </Button>
        {config && (
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => {
              if (
                confirm(
                  'Slack-Konfiguration dieser Fahrzeuggruppe entfernen?\n\n' +
                    'Danach gehen keine Freigabe-Anfragen mehr in den Channel. Die Entscheidung ' +
                    'erreicht die buchende Person weiterhin als Direktnachricht.',
                )
              ) {
                remove.mutate();
              }
            }}
          >
            Entfernen
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Leere Einträge fallen weg, Groß-/Kleinschreibung wird zurechtgerückt.
 *
 * Slack-IDs sind durchweg groß; eine abgetippte Kleinschreibung soll nicht am
 * Schema scheitern, nachdem sie beim Empfängerfeld der Integrationsprüfung
 * ebenfalls angenommen wird.
 */
function splitIds(raw: string): string[] {
  return raw
    .split(',')
    .map((value) => value.trim().toUpperCase())
    .filter(Boolean);
}

function Toggle({
  checked,
  onChange,
  disabled,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  label: string;
  hint?: string;
}) {
  return (
    <label className="flex gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 rounded border-slate-300"
      />
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}
